import React from 'react';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { useUIStore, PersonaRole } from '../stores/useUIStore';
import { useDocumentStore } from '../stores/useDocumentStore';
import { ExplainerService } from '../services/ExplainerService';
import { Briefcase, Target, Cpu, Code2, Sparkles, CheckCircle2, ArrowRight, BookOpen, Layers, ShieldCheck, Compass } from 'lucide-react';

export const RoleExplainerView: React.FC = () => {
  const { currentPackage } = useWorkspaceStore();
  const { selectedRole, setSelectedRole, setActiveTab } = useUIStore();
  const { setActiveDocument } = useDocumentStore();

  if (!currentPackage) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 text-center font-mono text-xs text-slate-500">
        No specification package loaded.
      </div>
    );
  }

  const explanation = ExplainerService.synthesizeExplanation(currentPackage, selectedRole);

  const handleOpenDoc = (docPath: string) => {
    setActiveDocument(docPath);
    setActiveTab('reader');
  };

  const roles: { id: PersonaRole; title: string; icon: React.ReactNode; color: string }[] = [
    { id: 'executive', title: 'Executive / Sponsor', icon: <Briefcase className="w-4 h-4" />, color: 'from-amber-500/20 to-amber-600/10 border-amber-500/40 text-amber-300' },
    { id: 'po', title: 'Product Owner', icon: <Target className="w-4 h-4" />, color: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/40 text-emerald-300' },
    { id: 'architect', title: 'Lead Architect', icon: <Cpu className="w-4 h-4" />, color: 'from-cyan-500/20 to-cyan-600/10 border-cyan-500/40 text-cyan-300' },
    { id: 'developer', title: 'Senior Developer', icon: <Code2 className="w-4 h-4" />, color: 'from-blue-500/20 to-blue-600/10 border-blue-500/40 text-blue-300' }
  ];

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8 max-w-6xl mx-auto space-y-6 md:space-y-8">
      {/* Top Header & Role Selector Bar */}
      <div className="glass-panel p-6 rounded-2xl border border-white/10 space-y-6 shadow-2xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-white tracking-tight flex items-center gap-2 font-mono">
              <Compass className="w-5 h-5 text-blue-400" />
              Role-Based Specification Explainer
            </h2>
            <p className="text-xs text-slate-400 mt-1 font-mono">
              Synthesized briefing explaining what <span className="text-blue-300 font-bold">{currentPackage.name}</span> actually builds for your perspective.
            </p>
          </div>

          <div className="text-xs font-mono px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 self-start sm:self-auto shrink-0">
            Persona: <span className="font-bold text-white uppercase">{selectedRole}</span>
          </div>
        </div>

        {/* Persona Role Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {roles.map((r) => {
            const isSelected = selectedRole === r.id;
            return (
              <button
                key={r.id}
                onClick={() => setSelectedRole(r.id)}
                className={`p-3.5 rounded-xl border text-left transition-all flex items-center space-x-3 group ${
                  isSelected
                    ? `bg-gradient-to-r ${r.color} shadow-lg shadow-blue-500/10 ring-1 ring-white/20`
                    : 'bg-obsidian-900/60 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-200'
                }`}
              >
                <div className={`p-2 rounded-lg ${isSelected ? 'bg-white/10 text-white' : 'bg-slate-800 text-slate-400 group-hover:text-slate-200'}`}>
                  {r.icon}
                </div>
                <div>
                  <div className="text-xs font-bold font-mono tracking-tight text-white">{r.title}</div>
                  <div className="text-[10px] text-slate-400 font-mono truncate">{r.id.toUpperCase()} Perspective</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Explainer Briefing Card */}
      <div className="glass-panel p-6 rounded-2xl border border-blue-500/20 space-y-6 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none">
          <Sparkles className="w-48 h-48 text-blue-400" />
        </div>

        {/* Role Header Banner */}
        <div className="border-b border-white/10 pb-4">
          <span className="text-[10px] uppercase font-mono tracking-widest text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 px-2.5 py-1 rounded-full font-bold">
            {explanation.roleTitle} Briefing
          </span>
          <h3 className="text-lg font-bold text-white mt-2 font-mono">{explanation.tagline}</h3>
        </div>

        {/* Grid: What It Builds vs Business Impact */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-obsidian-900/80 p-5 rounded-xl border border-slate-800 space-y-2">
            <h4 className="text-xs font-bold text-blue-400 font-mono uppercase tracking-wider flex items-center gap-1.5">
              <Layers className="w-4 h-4" />
              What This System Builds
            </h4>
            <p className="text-xs text-slate-300 leading-relaxed">
              {explanation.whatItBuilds}
            </p>
          </div>

          <div className="bg-obsidian-900/80 p-5 rounded-xl border border-slate-800 space-y-2">
            <h4 className="text-xs font-bold text-emerald-400 font-mono uppercase tracking-wider flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4" />
              Value & Strategic Impact
            </h4>
            <p className="text-xs text-slate-300 leading-relaxed">
              {explanation.businessImpact}
            </p>
          </div>
        </div>

        {/* Key System Capabilities */}
        <div className="space-y-3 pt-2">
          <h4 className="text-xs font-bold text-slate-300 font-mono uppercase tracking-wider">
            Key Capabilities & System Deliverables
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {explanation.keyCapabilities.map((cap, idx) => (
              <div key={idx} className="flex items-start space-x-2.5 text-xs text-slate-300 bg-slate-900/60 p-3 rounded-xl border border-slate-800/80 font-mono">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <span>{cap}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recommended Reading Path Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-white font-mono uppercase tracking-wider flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-cyan-400" />
            Recommended Reading Path for {explanation.roleTitle}
          </h3>
          <span className="text-xs font-mono text-slate-400">Ordered by Priority</span>
        </div>

        <div className="space-y-3">
          {explanation.recommendedDocs.map((doc, idx) => {
            const badgeColor =
              doc.importance === 'Essential'
                ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                : doc.importance === 'Recommended'
                ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30'
                : 'bg-slate-800 text-slate-400 border-slate-700';

            return (
              <div
                key={doc.path}
                className="glass-panel p-4 rounded-xl border border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 hover:border-blue-500/40 transition-all group shadow-md"
              >
                <div className="space-y-1 max-w-2xl">
                  <div className="flex items-center space-x-2.5 flex-wrap gap-y-1">
                    <span className="text-xs font-mono font-bold text-slate-400">0{idx + 1}.</span>
                    <span className="text-xs font-bold text-white font-mono group-hover:text-blue-300 transition-colors">
                      {doc.title}
                    </span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full border font-bold uppercase ${badgeColor}`}>
                      {doc.importance}
                    </span>
                  </div>

                  <p className="text-xs text-slate-300 pl-0 sm:pl-6 leading-relaxed">
                    {doc.plainSummary}
                  </p>
                </div>

                <button
                  onClick={() => handleOpenDoc(doc.path)}
                  className="px-3.5 py-2 rounded-xl bg-blue-600/20 hover:bg-blue-600 text-blue-300 hover:text-white border border-blue-500/40 text-xs font-mono transition-all flex items-center space-x-1.5 shrink-0 shadow-sm self-end sm:self-auto min-h-[36px]"
                >
                  <span>Read Spec</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
